import { useAuthStore } from '../stores/auth.store';

const API_BASE = '/api';

interface ApiError {
  error: string;
  details?: unknown;
}

class ApiClient {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = useAuthStore.getState().token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));

      // Handle 401 - unauthorized
      if (response.status === 401) {
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
      }

      throw new Error(error.error || 'An error occurred');
    }

    return response.json();
  }

  async get<T>(path: string): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include',
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(path: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(path: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(path: string): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include',
    });

    return this.handleResponse<T>(response);
  }
}

export const api = new ApiClient();

// Type-safe API functions
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{
      success: boolean;
      user: { id: string; email: string; name: string | null; role: string };
      token: string;
      refreshToken: string;
    }>('/auth/login', { email, password }),

  register: (email: string, password: string, name?: string) =>
    api.post<{
      success: boolean;
      user: { id: string; email: string; name: string | null; role: string };
      token: string;
      refreshToken: string;
    }>('/auth/register', { email, password, name }),

  logout: () => api.post<{ success: boolean }>('/auth/logout'),

  me: () =>
    api.get<{
      user: { id: string; email: string; name: string | null; role: string };
    }>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ success: boolean; message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
    }),
};

export const clientsApi = {
  list: () =>
    api.get<{
      clients: Array<{
        id: string;
        slug: string;
        companyName: string;
        websiteUrl: string;
        city: string;
        niche: string;
        logoUrl: string | null;
        isActive: boolean;
        integrations: {
          wordpress: boolean;
          youtube: boolean;
          podbean: boolean;
          facebook: boolean;
          gbp: boolean;
        };
      }>;
    }>('/clients'),

  get: (slug: string) =>
    api.get<{ client: Record<string, unknown> }>(`/clients/${slug}`),

  create: (data: Record<string, unknown>) =>
    api.post<{ client: Record<string, unknown> }>('/clients', data),

  update: (slug: string, data: Record<string, unknown>) =>
    api.put<{ client: Record<string, unknown> }>(`/clients/${slug}`, data),

  delete: (slug: string) =>
    api.delete<{ success: boolean; message: string }>(`/clients/${slug}`),

  updateCredentials: (slug: string, data: Record<string, unknown>) =>
    api.put<{ success: boolean; message: string }>(
      `/clients/${slug}/credentials`,
      data
    ),
};

export const healthApi = {
  check: () =>
    api.get<{
      status: string;
      timestamp: string;
      version: string;
      checks: Record<string, { status: string; message?: string }>;
    }>('/health'),
};

export const workflowsApi = {
  create: (data: {
    clientId: string;
    paaText: string;
    internalLinks?: string[];
    pauseAfterBlog?: boolean;
  }) =>
    api.post<{
      success: boolean;
      workflow: {
        workflowId: string;
        status: string;
        currentStep: number;
        totalSteps: number;
      };
      message: string;
    }>('/workflows', data),

  getStatus: (workflowId: string) =>
    api.get<{
      workflowId: string;
      status: string;
      currentStep: number;
      totalSteps: number;
      currentStepName?: string;
      wordpressPostUrl?: string;
      blogTitle?: string;
      gmbDescription?: string;
      error?: string;
    }>(`/workflows/${workflowId}/status`),

  resume: (workflowId: string, data: { shareUrl: string }) =>
    api.post<{
      success: boolean;
      message: string;
    }>(`/workflows/${workflowId}/resume`, data),

  list: (params?: { page?: number; limit?: number; clientId?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.clientId) query.set('clientId', params.clientId);
    if (params?.status) query.set('status', params.status);
    return api.get<{
      workflows: Array<{
        id: string;
        paaText: string;
        status: string;
        currentStep: number;
        totalSteps: number;
        createdAt: string;
        client: { companyName: string };
      }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/workflows?${query.toString()}`);
  },
};
